
import { Link } from 'react-router-dom';
import Header from '../../../components/feature/Header';
import Footer from '../../../components/feature/Footer';
import { useEffect } from 'react';

export default function BothasigPage() {
  useEffect(() => {
    // Update page title and meta tags
    document.title = "Bothasig Painters Cape Town | Professional Residential & Commercial Painting Services";

    // Update meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Professional painting services for Bothasig properties. Expert residential & commercial painting, interior & exterior work. Trusted painters serving Bothasig Cape Town with quality finishes.');
    }

    // Update meta keywords
    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'Bothasig painters, residential painting Bothasig, commercial painting, Bothasig property painting, Cape Town painters');
    }

    // Update canonical URL
    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) {
      canonical.setAttribute('href', `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/areas/bothasig`);
    }

    // Add Schema.org JSON-LD
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": "Bothasig Painters - Professional Painting Services Cape Town",
      "description": "Professional painting services for Bothasig residential and commercial properties with quality finishes.",
      "url": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/areas/bothasig`,
      "mainEntity": {
        "@type": "LocalBusiness",
        "name": "Cape Town Painters - Bothasig",
        "description": "Expert residential and commercial painting services for Bothasig properties.",
        "areaServed": "Bothasig, Cape Town",
        "serviceType": "Residential and Commercial Painting Services"
      }
    });
    document.head.appendChild(script);

    return () => {
      const existingScript = document.querySelector('script[type="application/ld+json"]');
      if (existingScript && existingScript.textContent?.includes('Bothasig')) {
        existingScript.remove();
      }
    };
  }, []);

  const services = [
    {
      icon: 'ri-home-line',
      title: 'Family Home Painting',
      description: 'Complete interior and exterior painting for Bothasig family homes and residential properties. We specialize in creating beautiful, durable finishes that enhance your home\'s value and curb appeal.',
      price: 'From R7,500',
      features: ['Interior wall painting', 'Exterior facade painting', 'Ceiling treatments', 'Trim and detail work', 'Color consultation']
    },
    {
      icon: 'ri-building-line',
      title: 'Townhouse Complexes',
      description: 'Large-scale painting projects for Bothasig townhouse developments and residential estates. We coordinate with body corporates and manage complex multi-unit projects efficiently.',
      price: 'From R20,000',
      features: ['Multi-unit coordination', 'Body corporate liaison', 'Scheduled phasing', 'Uniform color schemes', 'Minimal resident disruption']
    },
    {
      icon: 'ri-brush-line',
      title: 'Maintenance Painting',
      description: 'Regular maintenance and touch-up services for Bothasig residential properties. Keep your home looking fresh with our ongoing maintenance programs.',
      price: 'From R3,500',
      features: ['Annual touch-ups', 'Weather damage repair', 'Preventive maintenance', 'Quick response service', 'Flexible scheduling']
    }
  ];

  const paintingProcess = [
    {
      step: 1,
      title: 'Property Assessment',
      description: 'Comprehensive evaluation of your Bothasig property, including surface conditions, existing paint quality, and environmental factors affecting the suburban home.'
    },
    {
      step: 2,
      title: 'Surface Preparation',
      description: 'Thorough cleaning, sanding, and priming of all surfaces. Special attention to areas affected by Cape Town\'s seasonal weather patterns and suburban environmental conditions.'
    },
    {
      step: 3,
      title: 'Quality Application',
      description: 'Professional application using premium paints suitable for Bothasig\'s climate. Multiple coats ensure durability and long-lasting beauty for your family home.'
    },
    {
      step: 4,
      title: 'Final Inspection',
      description: 'Detailed quality check and cleanup. We ensure your Bothasig property meets our high standards before project completion and handover.'
    }
  ];

  const testimonials = [
    {
      name: 'Sarah Johnson',
      property: 'Family Home',
      rating: 5,
      text: 'Excellent service for our Bothasig home. The team was professional, punctual, and delivered outstanding results. Our neighbors have been asking for their contact details! The quality of work exceeded our expectations and the house looks absolutely beautiful.',
      image: 'https://public.readdy.ai/ai/img_res/9ddbcff03c553f84d09a519545b8a06f.jpg'
    },
    {
      name: 'Michael van Wyk',
      property: 'Townhouse Complex',
      rating: 5,
      text: 'Great work on our townhouse exterior. They coordinated well with the body corporate and completed the work efficiently with minimal disruption to residents. The project management was exceptional and the results speak for themselves.',
      image: 'https://readdy.ai/api/search-image?query=Professional%20man%20homeowner%20at%20Bothasig%20townhouse%20complex%2C%20freshly%20painted%20exterior%2C%20suburban%20residential%20area%2C%20satisfied%20client%20portrait&width=80&height=80&seq=bothasig2new&orientation=squarish'
    },
    {
      name: 'Linda Pieterse',
      property: 'Retirement Home',
      rating: 5,
      text: 'Wonderful experience from start to finish. The painters were respectful, clean, and the quality of work is exceptional. They understood the special requirements of our retirement community and worked around our schedules perfectly.',
      image: 'https://readdy.ai/api/search-image?query=Elderly%20woman%20homeowner%20in%20Bothasig%20retirement%20home%20with%20newly%20painted%20interior%2C%20comfortable%20suburban%20setting%2C%20satisfied%20customer%20portrait&width=80&height=80&seq=bothasig5new&orientation=squarish'
    }
  ];

  const projects = [
    {
      title: 'Suburban Family Homes',
      description: 'Complete interior and exterior painting for multiple family homes with durable finishes and modern color schemes. These projects showcase our expertise in residential painting for Bothasig\'s diverse housing stock.',
      image: 'https://readdy.ai/api/search-image?query=Bothasig%20suburban%20family%20homes%20with%20freshly%20painted%20exteriors%2C%20tree-lined%20residential%20street%2C%20modern%20family%20neighborhoods%2C%20professional%20painting%20work&width=400&height=300&seq=bothasig3new&orientation=landscape'
    },
    {
      title: 'Townhouse Development',
      description: 'Large-scale exterior renovation project with coordinated color scheme across entire residential complex. This project demonstrates our ability to manage complex multi-unit developments efficiently.',
      image: 'https://readdy.ai/api/search-image?query=Bothasig%20townhouse%20development%20with%20newly%20painted%20exteriors%2C%20suburban%20residential%20complex%2C%20coordinated%20color%20scheme%2C%20professional%20maintenance%20work&width=400&height=300&seq=bothasig4new&orientation=landscape'
    },
    {
      title: 'Retirement Village',
      description: 'Comprehensive painting and maintenance program for senior living facilities with accessible design considerations. Special attention to safety and minimal disruption for elderly residents.',
      image: 'https://readdy.ai/api/search-image?query=Bothasig%20retirement%20village%20with%20professional%20exterior%20painting%2C%20senior-friendly%20residential%20facility%2C%20peaceful%20suburban%20setting%2C%20quality%20finish%20work&width=400&height=300&seq=bothasig6new&orientation=landscape'
    },
    {
      title: 'Commercial Office Park',
      description: 'Professional commercial painting project for Bothasig business district with modern corporate finishes. Demonstrates our versatility in both residential and commercial applications.',
      image: 'https://readdy.ai/api/search-image?query=Bothasig%20commercial%20office%20park%20with%20freshly%20painted%20exteriors%2C%20business%20district%2C%20modern%20commercial%20buildings%2C%20professional%20painting%20finish&width=400&height=300&seq=bothasig7new&orientation=landscape'
    }
  ];

  const environmentalFactors = [
    {
      title: 'Suburban Climate Conditions',
      description: 'Bothasig\'s inland suburban location presents unique painting challenges including temperature variations, seasonal weather patterns, and urban environmental factors that require specialized paint selection and application techniques.'
    },
    {
      title: 'Residential Density Considerations',
      description: 'The high density of residential properties in Bothasig requires careful project planning to minimize disruption to neighbors while maintaining access for equipment and materials in established neighborhoods.'
    },
    {
      title: 'Family-Friendly Requirements',
      description: 'Many Bothasig properties house families with children, requiring the use of low-VOC, child-safe paints and scheduling that accommodates family routines and school schedules.'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero Section */}
      <section 
        className="relative bg-cover bg-center bg-no-repeat min-h-screen flex items-center"
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.4), rgba(0, 0, 0, 0.4)), url('https://readdy.ai/api/search-image?query=Professional%20painters%20working%20on%20residential%20property%20in%20Bothasig%20suburb%20with%20typical%20Cape%20Town%20suburban%20homes%2C%20painting%20equipment%2C%20scaffolding%2C%20family%20neighborhoods%2C%20sunny%20day&width=1920&height=1080&seq=bothasig_hero2&orientation=landscape')`
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <div className="max-w-3xl">
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-6 leading-tight">
              Professional Painters Bothasig
            </h1>
            <p className="text-xl md:text-2xl text-gray-200 mb-8 leading-relaxed">
              Expert painting services for Bothasig family homes and residential properties. Quality finishes and reliable service for your suburban home with over 15 years of experience serving the Bothasig community.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                to="/services"
                className="bg-white text-black px-8 py-4 rounded-lg text-lg font-semibold hover:bg-gray-100 transition-colors text-center whitespace-nowrap cursor-pointer border border-gray-200"
              >
                Services
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* About Bothasig Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Painting Services for Bothasig Homes
              </h2>
              <p className="text-lg text-gray-600 mb-6">
                Bothasig is a well-established suburban area in Cape Town, known for its family-friendly neighborhoods, diverse housing options, and strong community spirit. From modern townhouse complexes to traditional family homes and retirement villages, Bothasig presents unique painting challenges that require local expertise and understanding.
              </p>
              <p className="text-lg text-gray-600 mb-6">
                Our team has been serving the Bothasig community for over a decade, developing specialized techniques for the area's varied residential properties. We understand the importance of maintaining property values in this desirable suburb while ensuring minimal disruption to the close-knit community.
              </p>
              <p className="text-lg text-gray-600">
                Whether you're updating a family home, coordinating a townhouse complex renovation, or maintaining a retirement facility, our Bothasig painting specialists deliver exceptional results that enhance your property's beauty and longevity.
              </p>
            </div>
            <div>
              <img
                src="https://readdy.ai/api/search-image?query=Bothasig%20residential%20suburb%20with%20typical%20Cape%20Town%20suburban%20homes%2C%20family%20neighborhoods%2C%20tree-lined%20streets%2C%20residential%20properties%2C%20Bothasig%20community%20setting&width=600&height=400&seq=bothasig8new&orientation=landscape"
                alt="Bothasig Residential Area"
                className="w-full h-96 object-cover object-top rounded-xl shadow-lg"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Local Services Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Bothasig Painting Services
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Comprehensive painting solutions for Bothasig's residential properties and family homes, tailored to the unique needs of this vibrant suburban community.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <div key={index} className="bg-white rounded-xl shadow-lg p-8 hover:shadow-xl transition-shadow">
                <div className="w-16 h-16 flex items-center justify-center mx-auto mb-6" style={{backgroundColor: '#ffff00'}}>
                  <i className={`${service.icon} text-black text-2xl`}></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">{service.title}</h3>
                <p className="text-gray-600 mb-4">{service.description}</p>
                <div className="text-yellow-600 font-semibold text-lg mb-4">{service.price}</div>
                <ul className="text-sm text-gray-600 space-y-1">
                  {service.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center">
                      <div className="w-16 h-16 flex items-center justify-center mx-auto mb-6" style={{backgroundColor: '#ffff00'}}>
                        <i className={`${feature.icon} text-black text-2xl`}></i>
                      </div>
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Painting Process Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Our Bothasig Painting Process
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              A systematic approach developed specifically for Bothasig properties, ensuring quality results and minimal disruption to your family routine.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {paintingProcess.map((process, index) => (
              <div key={index} className="text-center">
                <div className="w-16 h-16 bg-yellow-400 text-black rounded-full flex items-center justify-center mx-auto mb-4 text-xl font-bold">
                  {process.step}
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">{process.title}</h3>
                <p className="text-gray-600">{process.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Environmental Factors Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Bothasig Environmental Considerations
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Understanding the unique environmental factors that affect painting projects in Bothasig's suburban setting.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {environmentalFactors.map((factor, index) => (
              <div key={index} className="bg-white rounded-xl shadow-lg p-8">
                <h3 className="text-xl font-bold text-gray-900 mb-4">{factor.title}</h3>
                <p className="text-gray-600">{factor.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us for Bothasig */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Why Choose Us for Bothasig?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Local expertise combined with professional excellence makes us the preferred choice for Bothasig homeowners and property managers.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 flex items-center justify-center mx-auto mb-6" style={{backgroundColor: '#ffff00'}}>
                <i className="ri-home-4-line text-black text-2xl"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Family Focused</h3>
              <p className="text-gray-600">Understanding the needs of Bothasig families with child-safe paints, flexible scheduling, and minimal disruption to daily routines.</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 flex items-center justify-center mx-auto mb-6" style={{backgroundColor: '#ffff00'}}>
                <i className="ri-building-line text-black text-2xl"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Local Expertise</h3>
              <p className="text-gray-600">Extensive experience with Bothasig's residential properties, from modern townhouses to established family homes and retirement facilities.</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 flex items-center justify-center mx-auto mb-6" style={{backgroundColor: '#ffff00'}}>
                <i className="ri-community-line text-black text-2xl"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Affordable Pricing</h3>
              <p className="text-gray-600">Competitive rates for Bothasig homeowners with transparent pricing, detailed quotes, and no hidden costs or surprise charges.</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-yellow-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                <i className="ri-calendar-check-line text-2xl text-yellow-600"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Reliable Service</h3>
              <p className="text-gray-600">Dependable scheduling and timely completion for busy Bothasig families, with flexible timing to accommodate work and school schedules.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Recent Projects */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Recent Bothasig Projects
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Showcasing our recent work across Bothasig's diverse residential landscape, from family homes to commercial properties.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {projects.map((project, index) => (
              <div key={index} className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
                <img
                  src={project.image}
                  alt={project.title}
                  className="w-full h-64 object-cover object-top"
                />
                <div className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{project.title}</h3>
                  <p className="text-gray-600">{project.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Additional Environmental Image */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div>
          <img
            src="https://readdy.ai/api/search-image?query=Bothasig%20suburban%20environment%20with%20typical%20Cape%20Town%20weather%20conditions%2C%20residential%20properties%2C%20tree-lined%20streets%2C%20suburban%20challenges%20for%20painting%20maintenance&width=600&height=500&seq=bothasig9new&orientation=portrait"
            alt="Bothasig Residential Environment"
            className="w-full h-96 object-cover object-top rounded-xl shadow-lg"
          />
        </div>
      </div>

      {/* Testimonials */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Bothasig Client Reviews
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Hear from satisfied Bothasig homeowners who have experienced our professional painting services firsthand.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="bg-gray-50 rounded-xl p-8">
                <div className="flex items-center mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <i key={i} className="ri-star-fill text-yellow-400 text-lg"></i>
                  ))}
                </div>
                <p className="text-gray-700 mb-6 italic">"{testimonial.text}"</p>
                <div className="flex items-center">
                  <img
                    src={testimonial.image}
                    alt={testimonial.name}
                    className="w-12 h-12 rounded-full object-cover mr-4"
                  />
                  <div>
                    <h4 className="font-semibold text-gray-900">{testimonial.name}</h4>
                    <p className="text-gray-600 text-sm">{testimonial.property}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-yellow-400">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-black mb-6">
            Ready to Paint Your Bothasig Home?
          </h2>
          <p className="text-xl text-black/80 mb-8 max-w-3xl mx-auto">
            Contact Cape Town's trusted residential painting experts for Bothasig. Get your free estimate today and discover why families throughout Bothasig choose us for their painting needs.
          </p>
          <div className="flex flex-col sm:flex-row gap-6">
            <a
              href="/contact"
              className="bg-teal-600 text-white px-8 py-4 rounded-lg text-xl font-bold hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer"
            >
              Get Free Quote
            </a>
          </div>
          <div className="flex justify-center mt-6">
            <a
              href="mailto:Get a quote"
              className="bg-black text-white px-8 py-4 rounded-lg text-xl font-bold hover:bg-gray-800 transition-colors whitespace-nowrap cursor-pointer"
            >
              Get Quote
            </a>
          </div>
        </div>
      </section>

      <Footer />

      {/* Elfsight Popup */}
      <div className="elfsight-app-a8d09b4b-1f9c-47af-9c95-257e6e1aa6a7" data-elfsight-app-lazy></div>
    </div>
  );
}
